/**
 * Calls API – token and cold-start.
 * Backend returns liveKitWsUrl; no frontend env vars for LiveKit.
 */
import { apiClient } from '@/shared/services/api';

/**
 * GET /calls/:callId/token – cold start when user accepts and app doesn't have token.
 * @param {string} callId
 * @returns {Promise<{ canJoin: boolean, token?: string, liveKitRoomId?: string, liveKitWsUrl?: string }>}
 */
export async function getCallToken(callId) {
  const { data } = await apiClient.get(`/calls/${encodeURIComponent(callId)}/token`);
  return {
    canJoin: data?.canJoin ?? false,
    token: data?.token,
    liveKitRoomId: data?.liveKitRoomId,
    liveKitWsUrl: data?.liveKitWsUrl,
  };
}

/**
 * GET /calls/active/check – whether the current user has a RINGING or ANSWERED call.
 * @returns {Promise<{ hasActiveCall: boolean, call: object | null }>}
 */
export async function checkActiveCall() {
  const { data } = await apiClient.get('/calls/active/check');
  return {
    hasActiveCall: Boolean(data?.hasActiveCall),
    call: data?.call ?? null,
  };
}

/**
 * POST /calls/:callId/end – hang up (idempotent). Works for caller or callee.
 * @param {string} callId
 * @param {string} [reason] – e.g. COMPLETED (backend CallEndReason)
 */
export async function endCallRest(callId, reason = 'COMPLETED') {
  await apiClient.post(`/calls/${encodeURIComponent(callId)}/end`, {
    reason,
  });
}

/**
 * POST /calls/:callId/cancel – caller cancels while still RINGING.
 * @param {string} callId
 */
export async function cancelCallRest(callId) {
  await apiClient.post(`/calls/${encodeURIComponent(callId)}/cancel`, {});
}

/**
 * POST /calls/:callId/reject-from-notification – reject when socket is unavailable (same as mobile).
 * @param {string} callId
 */
export async function rejectCallFromNotificationRest(callId) {
  await apiClient.post(`/calls/${encodeURIComponent(callId)}/reject-from-notification`, {});
}

/**
 * Clear the user's server-side active call using real backend routes (no /calls/active/clear).
 * Uses GET /calls/active/check then POST cancel (caller + RINGING) or POST end.
 *
 * @param {string} [currentUserId] – when set, used to choose cancel vs end for RINGING calls
 * @returns {Promise<{ cleared: number, callIds: string[] }>}
 */
export async function clearActiveCalls(currentUserId) {
  const { hasActiveCall, call } = await checkActiveCall();
  if (!hasActiveCall || !call?.id) {
    return { cleared: 0, callIds: [] };
  }
  const callId = call.id;
  const status = String(call.status || '').toUpperCase();
  const isCaller =
    currentUserId != null &&
    String(call.callerId || '') === String(currentUserId);
  if (status === 'RINGING' && isCaller) {
    await cancelCallRest(callId);
  } else {
    await endCallRest(callId, 'COMPLETED');
  }
  return { cleared: 1, callIds: [callId] };
}

/**
 * POST /calls/:callId/refresh-token – refresh LiveKit token for long-running calls.
 * Returns a new token and (optionally) updated LiveKit room info.
 * @param {string} callId
 * @returns {Promise<{ token: string | undefined, liveKitWsUrl: string | undefined, liveKitRoomId: string | undefined }>}
 */
export async function refreshCallToken(callId) {
  const { data } = await apiClient.post(`/calls/${encodeURIComponent(callId)}/refresh-token`);
  return {
    token: data?.token,
    liveKitWsUrl: data?.liveKitWsUrl,
    liveKitRoomId: data?.liveKitRoomId,
  };
}

/**
 * GET /calls/history – paginated call history for the current user.
 * @param {{ limit?: number, cursor?: string | null }} params
 * @returns {Promise<{ calls: any[], nextCursor: string | null, hasMore: boolean }>}
 */
export async function getCallHistory(params = {}) {
  const { limit = 20, cursor } = params;
  const query = { limit };
  if (cursor) query.cursor = cursor;
  const { data } = await apiClient.get('/calls/history', { params: query });
  return {
    calls: data?.calls ?? [],
    nextCursor: data?.nextCursor ?? null,
    hasMore: Boolean(data?.hasMore),
  };
}

/**
 * GET /calls/analytics/summary – summary metrics for the current user's calls.
 * @returns {Promise<any>}
 */
export async function getCallMetricsSummary() {
  const { data } = await apiClient.get('/calls/analytics/summary');
  return data;
}
